<template>
  <UmisisModal
    ref="descriptionModal"
    :ok-disabled="isUpdating"
    title="Update Template Description"
    @ok="submitDescription"
    @show="onModalShow"
  >
    <Field>
      <FieldLabel for="task-template-description-input">
        Description
      </FieldLabel>
      <Textarea
        id="task-template-description-input"
        v-model="draftDescription"
        rows="4"
        class="min-h-24"
        placeholder="Enter template description"
      />
      <FieldDescription>Used as the default description for generated tasks.</FieldDescription>
    </Field>
  </UmisisModal>
</template>

<script setup>
import { computed, ref } from 'vue'
import { toast } from 'vue-sonner'
import UmisisModal from '../../../../../../../../resources/js/components/umisis/generic/UmisisModal.vue'
import { useModalControl } from '../../../../../../../../resources/js/composables/modal/useUmisisModal.js'
import { Field, FieldDescription, FieldLabel } from '../../../../../../../../resources/js/components/ui/field/index.js'
import { Textarea } from '../../../../../../../../resources/js/components/ui/textarea/index.js'
import { useTaskTemplateStore } from '../../../stores/taskTemplateStore.js'

const props = defineProps({
  taskTemplateId: {
    type: [Number, String],
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  context: {
    type: String,
    default: 'task-template-show',
  },
})

const draftDescription = ref('')
const { componentRef: descriptionModal, openModal: openDialog, closeModal: closeDialog } = useModalControl()

const taskTemplateStore = useTaskTemplateStore(props.context)
const normalizedTaskTemplateId = computed(() => Number(props.taskTemplateId))
const isUpdating = computed(() => taskTemplateStore.isUpdatingDescription(normalizedTaskTemplateId.value))

const openModal = () => {
  openDialog()
}

const closeModal = () => {
  closeDialog()
}

const onModalShow = () => {
  draftDescription.value = props.description ?? ''
}

const submitDescription = async (event) => {
  event?.preventDefault?.()

  if (!Number.isInteger(normalizedTaskTemplateId.value) || normalizedTaskTemplateId.value <= 0) {
    return
  }

  const nextDescriptionRaw = draftDescription.value ?? ''

  if (nextDescriptionRaw === props.description) {
    closeDialog()
    return
  }

  const nextDescription = nextDescriptionRaw.trim() === '' ? null : nextDescriptionRaw

  try {
    await taskTemplateStore.updateDescription(normalizedTaskTemplateId.value, nextDescription)
    toast.success('Template description updated.')
    closeDialog()
  } catch (error) {
    const errorMessage = error?.response?.data?.message
      || error?.response?.data?.errors?.description?.[0]
      || 'Unable to update template description.'

    toast.error(errorMessage)
  }
}

defineExpose({
  openModal,
  closeModal,
})
</script>
